/**
 * Surveys — a form with an audience, a window and an anonymity promise.
 *
 * A survey's questions **are** a form of purpose `survey`, so nothing here re-describes a field
 * list: `formVersionId` points at a published version and the form builder owns the rest. What is
 * particular to a survey is who it is for, when it is open, and whether an answer can be traced
 * back to a person.
 */

import { z } from "zod";
import {
  instantSchema,
  longTextSchema,
  optionalText,
  shortTextSchema,
  ulidSchema,
} from "./primitives.js";

export const SURVEY_AUDIENCES = [
  "participants",
  "judges",
  "organizers",
  "volunteers",
  "vendors",
  "public",
] as const;

export const createSurveySchema = z
  .object({
    name: shortTextSchema,
    description: optionalText(longTextSchema),
    audience: z.enum(SURVEY_AUDIENCES).default("participants"),
    /**
     * Anonymity is a promise, not a display option. It is set at creation and enforced at write
     * time — an anonymous survey never records a participant against a response even when the link
     * that was followed knew exactly who was holding it.
     */
    anonymous: z.boolean().default(false),
    formVersionId: ulidSchema,
    opensAt: instantSchema.nullish(),
    closesAt: instantSchema.nullish(),
  })
  .refine(
    (value) => value.opensAt == null || value.closesAt == null || value.opensAt < value.closesAt,
    {
      message: "The survey has to open before it closes",
      path: ["closesAt"],
    },
  );

export const updateSurveySchema = z.object({
  name: shortTextSchema.optional(),
  description: longTextSchema.nullable().optional(),
  audience: z.enum(SURVEY_AUDIENCES).optional(),
  formVersionId: ulidSchema.optional(),
  opensAt: instantSchema.nullable().optional(),
  closesAt: instantSchema.nullable().optional(),
});

/**
 * A response. The answers are validated a second time against the form version's own schema, so
 * this only carries them through — a survey that asks a different community a different question
 * must not need a change here.
 */
export const surveyResponseSchema = z.object({
  answers: z.record(z.string(), z.unknown()),
});

export type CreateSurveyInput = z.infer<typeof createSurveySchema>;
export type SurveyAudience = (typeof SURVEY_AUDIENCES)[number];
